<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import AdminPage from "./components/AdminPage.vue";
import { canOpenAdmin, isReadOnlyProfile } from "./admin-read-only";
import MySignOffs from "./components/MySignOffs.vue";
import AuroraBackground from "./components/AuroraBackground.vue";
import AssistantHub from "./components/AssistantHub.vue";
import AssistantLanding from "./components/AssistantLanding.vue";
import BrandHeader from "./components/BrandHeader.vue";
import BudgetMeter from "./components/BudgetMeter.vue";
import ChatInput from "./components/ChatInput.vue";
import BidProjectPanel from "./components/BidProjectPanel.vue";
import UploadedDocsPanel from "./components/UploadedDocsPanel.vue";
import ConversationList from "./components/ConversationList.vue";
import AssistantPicker from "./components/AssistantPicker.vue";
import MessageBubble from "./components/MessageBubble.vue";
import ContractsDashboard from "./pages/ContractsDashboard.vue";
import DesignPreview from "./pages/DesignPreview.vue";
import KbManager from "./pages/KbManager.vue";
import KbPicker from "./components/KbPicker.vue";
import ProfileBuilder from "./pages/ProfileBuilder.vue";
import BidWorkspacePage from "./pages/BidWorkspace.vue";
import ServiceCatalogPage from "./pages/ServiceCatalog.vue";
import SourcePreviewPane from "./components/SourcePreviewPane.vue";
import ToastShelf from "./components/ToastShelf.vue";
import ConfirmDialog from "./components/ConfirmDialog.vue";
import PolicyGate from "./components/PolicyGate.vue";
import OnboardingGate from "./components/OnboardingGate.vue";
import UserPicker from "./components/UserPicker.vue";
import RefineAssistant from "./components/RefineAssistant.vue";
import { getMySignOffs, listWorkflows } from "./api/admin";
import { ChatError, fetchHealth, sendChat } from "./api/chat";
import {
  acknowledgePolicy,
  pendingOnboarding,
  checkPrompt,
  createConversation,
  getConversation,
  getMyProfile,
  listAssistants,
  type Assistant,
  type MyProfile,
} from "./api/conversations";
import { useConfirm } from "./composables/useConfirm";
import { assistantTint, iconOf } from "./assistant-visuals";
import { resolveAssistantIcon } from "./assistant-icons";
import { sourcePane } from "./stores/source-pane";
import type { ChatTurn, RetrievalEvent } from "./types/chat";
import { DEV_USERS } from "./dev-users";
import { acquireToken, USE_REAL_AUTH } from "./auth/msal";

// Hash-routing: minimal in-file parser — `#/admin`, `#/hub`,
// `#/assistant/<slug>`, `#/chat/<slug>`, `#/booking/new-profile`.
// No vue-router dep; five routes don't need a framework. Default
// (`#` or `#/`) is the Hub — the post-login landing surface from the
// DOIT AI vision deck.
type RouteName =
  | "hub"
  | "landing"
  | "chat"
  | "admin"
  | "booking"
  | "contracts"
  | "kbs"
  | "servicecatalog"
  | "bid"
  | "signoffs"
  | "preview";
interface ParsedRoute {
  name: RouteName;
  slug: string;
  seedQuery: string;
}

// Master switch for the Contracts dashboard (link + route + direct-URL
// access). Off until the fact-table figures are trustworthy again.
const CONTRACTS_DASHBOARD_ENABLED = false;

function parseHash(): ParsedRoute {
  const raw = window.location.hash.replace(/^#\/?/, "");
  if (raw.startsWith("admin"))
    return { name: "admin", slug: "", seedQuery: "" };
  if (raw.startsWith("hub")) return { name: "hub", slug: "", seedQuery: "" };
  // What a named signer or data owner signs or confirms themselves.
  if (raw.startsWith("sign-offs"))
    return { name: "signoffs", slug: "", seedQuery: "" };
  // CBM port Phase 3e — `#/booking/new-profile` opens the Booking
  // Matcher Profile Builder page. Only one sub-route today; `slug`
  // is the literal "new-profile" so future shapes (e.g. browse,
  // bulk-import) can extend it.
  const bookingMatch = raw.match(/^booking\/([^/?]+)/);
  if (bookingMatch) {
    return { name: "booking", slug: bookingMatch[1], seedQuery: "" };
  }
  // Contracts dashboard is hidden for now — its figures are stale after the
  // fact-table logic change (fact_contracts / fact_contract_facts). Flip
  // CONTRACTS_DASHBOARD_ENABLED back to true once the data is trustworthy.
  if (CONTRACTS_DASHBOARD_ENABLED && raw.startsWith("contracts/dashboard")) {
    return { name: "contracts", slug: "", seedQuery: "" };
  }
  if (raw.startsWith("kbs")) {
    return { name: "kbs", slug: "", seedQuery: "" };
  }
  // ADR 0060 phase 5 — the Servicekatalog assistant opens on its own
  // quote-editor page; the chat is a helper panel inside it.
  if (raw.startsWith("service-catalog")) {
    return { name: "servicecatalog", slug: "service-catalog", seedQuery: "" };
  }
  // Bid workspace — `#/bid` lists the bids, `#/bid/<uid>` opens one
  // (uid = "2026-113", the Bid Office folder number). Same shape as the
  // service catalogue: the page is the data, the chat is a helper inside it.
  const bidMatch = raw.match(/^bid(?:\/([0-9]{4}-[0-9]{1,5}))?/);
  if (bidMatch) {
    return { name: "bid", slug: bidMatch[1] ?? "", seedQuery: "" };
  }
  // Dev-only visual harness for the redesign (mock data, no auth/backend).
  // Gated to `vite dev` so it can never be reached in a production build.
  if (import.meta.env.DEV && raw.startsWith("preview")) {
    return { name: "preview", slug: "", seedQuery: "" };
  }
  const landingMatch = raw.match(/^assistant\/([^/?]+)/);
  if (landingMatch)
    return { name: "landing", slug: landingMatch[1], seedQuery: "" };
  const chatMatch = raw.match(/^chat\/([^/?]+)(?:\?(.*))?/);
  if (chatMatch) {
    const params = new URLSearchParams(chatMatch[2] ?? "");
    return {
      name: "chat",
      slug: chatMatch[1],
      seedQuery: params.get("q") ?? "",
    };
  }
  // Empty / unknown hash → hub (new default landing).
  return { name: "hub", slug: "", seedQuery: "" };
}
const route = ref<ParsedRoute>(parseHash());
function readRoute() {
  route.value = parseHash();
}
onMounted(() => window.addEventListener("hashchange", readRoute));
onUnmounted(() => window.removeEventListener("hashchange", readRoute));
function gotoAdmin() {
  window.location.hash = "#/admin?tab=assistants";
}
function exitAdmin() {
  window.location.hash = "#/hub";
}
function gotoHub() {
  window.location.hash = "#/hub";
}
function gotoContracts() {
  window.location.hash = "#/contracts/dashboard";
}
function gotoKbs() {
  window.location.hash = "#/kbs";
}

// Default to the admin dev user in local-dev mode so the operator
// lands on the Hub with both admin rights AND the seeded compliance
// /hr assistants visible (admin@local.test is in all three groups
// per DEFAULT_DEV_GROUPS). USE_REAL_AUTH=true wipes this out — the
// real Entra token is the identity in prod.
const selectedUser = ref(!USE_REAL_AUTH ? "admin@local.test" : "");
/** Template ref on the nav spend meter, so a finished chat turn can refresh
 * it immediately instead of waiting for the next poll. */
const budgetMeter = ref<{ refresh: () => Promise<void> } | null>(null);
// Deployments fronted by an auth proxy (TRUST_UPSTREAM_IDENTITY — PCAI's
// oauth2-proxy, or any nginx auth_request setup) run the SPA in its
// dev-mode build but the backend derives identity from proxy headers and
// ignores X-Dev-User. Showing the impersonation picker there is pure
// confusion, so hide it (and stop sending the ignored header) as soon as
// /api/health reports dev auth is off. Until health answers, dev-mode
// behavior is unchanged.
const devPickerVisible = ref(!USE_REAL_AUTH);
onMounted(async () => {
  const h = await fetchHealth();
  if (h && !h.dev_auth_enabled) {
    devPickerVisible.value = false;
    selectedUser.value = "";
  }
});
const turns = reactive<ChatTurn[]>([]);
const busy = ref(false);
const abort = ref<AbortController | null>(null);
const scroller = useTemplateRef<HTMLElement>("scroller");
const assistantSlug = ref("");
const conversationId = ref<number | null>(null);
const convListRefresh = ref(0);
// On mobile the conversation list collapses into an off-canvas drawer
// (it otherwise stacks above the chat and buries the composer). Desktop
// ignores this — the list is always an inline sidebar there.
const chatNavOpen = ref(false);

// Task #276 — upload status toast for the General assistant.
const uploadStatus = ref<string>("");
// Bumped after each successful upload so UploadedDocsPanel refetches.
const uploadsRefreshKey = ref(0);

// ADR 0019 §UI — the conversation's selected KB ids, written by the
// KbPicker chip-row. Reset when the conversation changes; the picker
// component is the only writer (it also calls the PATCH endpoint).
const conversationKbIds = ref<number[]>([]);
function onKbPickerError(msg: string) {
  uploadStatus.value = msg;
}

type UploadResult = {
  filename?: string;
  count?: number;
  intake_suggested?: boolean;
  chunk_count?: number;
  estimated_tokens?: number;
  /** pending | indexing | ready | failed. Indexing runs AFTER the upload
   * response, so a fresh upload is always chunk_count 0 / "pending".
   * Inferring failure from chunk_count === 0 would flag every single
   * upload as broken. */
  index_status?: string | null;
  /** Set when the file stored but could not be indexed — the document is
   * then readable by name but will never turn up in a search, which is
   * indistinguishable from "the assistant ignored my file" unless we say so. */
  index_error?: string | null;
};

/**
 * Uploads still on their way to the server. A message sent before its
 * attachment lands is answered without it: the turn lists the conversation's
 * files when it starts, and a file stored a second later is not in that list.
 * Pasting made this easy to hit — paste, Enter, and the send beat the upload
 * (feedback #224, 2026-10-27: "I did upload the screenshot, but it did not
 * see it"; the turn started 0.25 s before the upload was stored). onSubmit
 * waits for these instead of racing them.
 */
const inflightUploads = new Set<Promise<void>>();

async function onUpload(files: File | File[]) {
  const p = uploadFiles(files);
  inflightUploads.add(p);
  try {
    await p;
  } finally {
    inflightUploads.delete(p);
  }
}

async function uploadFiles(files: File | File[]) {
  const list = Array.isArray(files) ? files : [files];
  // Attaching the document you want to ask about is the natural FIRST move,
  // so create the conversation on demand rather than refusing the upload
  // until a message exists. The old guard told people to "send a first
  // message" — which meant asking a question the assistant could not yet
  // answer, getting a wrong answer, and only then attaching the file.
  if (conversationId.value === null) {
    if (!assistantSlug.value) {
      uploadStatus.value = "Pick an assistant first, then attach your file.";
      return;
    }
    uploadStatus.value = "Starting a conversation for this file…";
    const conv = await createConversation(
      assistantSlug.value,
      selectedUser.value || undefined,
    );
    if (conv === null) {
      uploadStatus.value =
        "Could not start a conversation for this upload — please try again.";
      return;
    }
    conversationId.value = conv.id;
    convListRefresh.value += 1;
  }
  const headers: Record<string, string> = {};
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    if (t) headers["Authorization"] = `Bearer ${t.token}`;
  } else if (selectedUser.value) {
    headers["X-Dev-User"] = selectedUser.value;
  }
  let uploaded = 0;
  let lastDoc: UploadResult | null = null;
  for (const file of list) {
    const rel =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
      file.name;
    uploadStatus.value = `Uploading ${rel}…`;
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (rel && rel !== file.name) fd.append("relative_path", rel);
      const r = await fetch(
        `${import.meta.env.VITE_API_BASE ?? ""}/api/conversations/${conversationId.value}/uploads`,
        { method: "POST", body: fd, headers },
      );
      if (!r.ok) {
        const body = await r.text();
        throw new Error(`${r.status} — ${body.slice(0, 200)}`);
      }
      lastDoc = (await r.json()) as UploadResult;
      uploaded += lastDoc.count ?? 1;
    } catch (e: unknown) {
      uploadStatus.value = `Upload failed: ${e instanceof Error ? e.message : String(e)}`;
      return;
    }
  }
  uploadsRefreshKey.value += 1;
  // A file that stored but did not index is the worst failure mode here: the
  // assistant cannot find it, and to the user that looks like the assistant
  // ignoring them. Say it plainly rather than reporting success.
  if (lastDoc?.index_error) {
    uploadStatus.value =
      `Uploaded ${lastDoc.filename ?? "file"}, but it could not be indexed — ` +
      `it will not be found by search. ${lastDoc.index_error}`;
    return;
  }
  // Indexing now runs in the background, so chunk_count is 0 on EVERY
  // fresh upload. Saying "no searchable text was extracted" here — as
  // this did — would call every successful upload a failure. The document
  // is usable immediately regardless: the assistant reads it from storage,
  // not from the search index.
  if (uploaded > 0 && (lastDoc?.index_status ?? "pending") !== "ready") {
    uploadStatus.value =
      `Uploaded ${lastDoc?.filename ?? "file"} — you can ask about it now. ` +
      `Preparing it for search in the background.`;
    return;
  }
  const intake = lastDoc?.intake_suggested
    ? " A] loaded — ask for tender_intake when ready."
    : " Ask about it.";
  uploadStatus.value = `Uploaded ${uploaded} file${uploaded === 1 ? "" : "s"}.${intake}`;
  setTimeout(() => {
    if (uploadStatus.value.startsWith("Uploaded ")) uploadStatus.value = "";
  }, 8000);
}

// Fetch the assistant list once so we can surface per-assistant sample prompts
// + placeholder text instead of hardcoding them in the shell.
const assistants = ref<Assistant[]>([]);
async function loadAssistants(): Promise<void> {
  try {
    assistants.value = await listAssistants(selectedUser.value || undefined);
  } catch {
    /* non-fatal — picker stays empty */
  }
}
onMounted(loadAssistants);
watch(() => selectedUser.value, loadAssistants);

// Caller's profile — pulled from /api/profile on mount and refreshed
// when the dev-user picker switches identities. `groups` is the
// authoritative resolver-output set the SPA reads to gate optional
// nav items (Contracts dashboard link, etc.). null = not loaded yet;
// nav items default to hidden until we know what the user can see.
const myProfile = ref<MyProfile | null>(null);
async function refreshMyProfile() {
  try {
    myProfile.value = await getMyProfile(selectedUser.value || undefined);
  } catch {
    myProfile.value = null;
  }
}
onMounted(refreshMyProfile);
watch(() => selectedUser.value, refreshMyProfile);

// How many agreements and confirmations wait for THIS person. The nav link
// shows only when something does: most people are never named, and a link
// to an always-empty page is noise. Failure leaves it hidden.
const mySignOffCount = ref(0);
async function refreshMySignOffs() {
  try {
    mySignOffCount.value = (
      await getMySignOffs(selectedUser.value || undefined)
    ).count;
  } catch {
    mySignOffCount.value = 0;
  }
}
onMounted(refreshMySignOffs);
watch(() => selectedUser.value, refreshMySignOffs);
function gotoSignOffs() {
  window.location.hash = "#/sign-offs";
}

// AI001 §4/§5/§6 — the first-use gate. Shown when the deployment has set a
// policy version and this user has not acknowledged that version. Empty
// version = the gate is off, for deployments that evidence training some
// other way.
const needsPolicyAck = computed<boolean>(() => {
  const pol = myProfile.value?.ai_policy;
  if (!pol?.version) return false;
  return pol.acknowledged !== pol.version;
});

async function onPolicyAcknowledged(): Promise<void> {
  await acknowledgePolicy(selectedUser.value || undefined);
  // Re-read rather than patching locally: the server owns the record, and
  // if the write did not land the gate must stay up rather than appear
  // accepted.
  await refreshMyProfile();
}

// EU AI Act Art. 4 — the onboarding video(s) for the assistant being
// opened: its landing page or its chat. After the policy gate, never with it.
// /api/chat refuses the same turn (428), so this is the friendly half.
const onboardingSlug = computed<string>(() => {
  if (route.value.name === "landing") return route.value.slug ?? "";
  if (route.value.name === "chat") return assistantSlug.value;
  return "";
});
const pendingVideo = computed(() =>
  needsPolicyAck.value ? null : pendingOnboarding(myProfile.value, onboardingSlug.value),
);

function leaveOnboarding(): void {
  window.location.hash = "#/";
}

const canSeeContracts = computed<boolean>(() => {
  if (!CONTRACTS_DASHBOARD_ENABLED) return false;
  const p = myProfile.value;
  if (!p) return false;
  if (p.is_admin) return true;
  return p.groups.includes("group:contracts-confidential");
});

// When the route is `#/chat/<slug>`, pre-select that assistant so
// the chat shell opens against it directly. Watching `route` keeps
// this in sync with back/forward navigation.
//
// When the route also carries `?q=<seed>` (from clicking a sample
// chip on the landing page), auto-submit that query once the shell
// is mounted, then strip the `?q=` from the URL so back-navigation
// doesn't re-fire it.
function applyRoute(r: ParsedRoute) {
  if (r.name === "chat" && r.slug) {
    // Any navigation INTO a chat (hub tile, landing sample, deep link)
    // starts a FRESH conversation with that assistant — clear the prior
    // transcript + conversation id so you never keep viewing (or worse,
    // continue, sending a turn into the wrong conversation) the
    // previously-active chat, possibly under a different assistant.
    // Resetting even for the same slug is intentional: clicking a tile
    // means "start something new here". Resuming a specific past thread
    // is the sidebar's job — selectConversation sets state directly and
    // never touches the hash, so it never routes through here.
    resetTurns();
    assistantSlug.value = r.slug;
    if (r.seedQuery) {
      const seed = r.seedQuery;
      history.replaceState(null, "", `#/chat/${r.slug}`);
      void nextTick(() => onSubmit(seed));
    }
  }
}
watch(route, applyRoute);
onMounted(() => applyRoute(route.value));

const selectedAssistant = computed<Assistant | null>(() => {
  if (!assistantSlug.value) return null;
  return assistants.value.find((a) => a.slug === assistantSlug.value) ?? null;
});

// ── ADR 0038 — SME entry point in the chat header ─────────────────────────
// "Adjust assistant" shows only when the backend confirms the caller
// may refine (admin or the assistant's owner) — probed via the
// workflows list, whose endpoint carries exactly that gate. 403 (or
// any failure) just hides the affordance; the server stays the
// authoritative gate either way.
const canRefine = ref(false);
const refineOpen = ref(false);
watch(
  [assistantSlug, selectedUser],
  async ([slug]) => {
    refineOpen.value = false;
    canRefine.value = false;
    if (!slug) return;
    try {
      await listWorkflows(slug, selectedUser.value);
      canRefine.value = true;
    } catch {
      canRefine.value = false;
    }
  },
  { immediate: true },
);

/** True when the selected assistant has either of the upload-consuming
 * chat tools wired (`read_uploaded_doc`, `search_uploaded_docs`). Used
 * to gate the paperclip / drag-drop / file-list panel — any assistant
 * can opt in just by listing the tools in its YAML, no slug check. */
const uploadsEnabled = computed<boolean>(() => {
  const tools = (selectedAssistant.value?.settings?.tools ?? []) as string[];
  return (
    tools.includes("read_uploaded_doc") ||
    tools.includes("search_uploaded_docs")
  );
});

const folderUploadsEnabled = computed<boolean>(
  () => uploadsEnabled.value && assistantSlug.value === "bid-writing",
);

// ADR 0019 §UI — KbPicker is offered when the assistant opted into the
// personal-KB search tool. Other tools (search_content, find_profiles,
// run_sql, etc.) don't read kb_ids, so the picker would be noise.
const kbPickerEnabled = computed<boolean>(() => {
  const tools = (selectedAssistant.value?.settings?.tools ?? []) as string[];
  return tools.includes("search_personal_kb");
});

const samples = computed<string[]>(() => {
  const raw = selectedAssistant.value?.settings?.samples;
  const fromOperator = Array.isArray(raw)
    ? raw.filter((s): s is string => typeof s === "string")
    : [];
  if (fromOperator.length) return fromOperator;
  // No operator-supplied samples → leave the chips empty. Earlier
  // fallback used eRegnskab-flavoured questions ("Top contributors by
  // total hours last quarter?") that bled across into every assistant
  // with tool-use enabled — confusing for users on a Booking or AI
  // Governance card. Better to show nothing than to show domain-wrong
  // samples; each assistant's YAML can declare its own
  // `settings.samples: [...]` when it actually wants chips.
  return [];
});

// Empty-state sample chips load the composer rather than sending. Operator
// samples are often templates ("… med <kunde>"), so firing them verbatim
// would ask the assistant about a customer named "<kunde>". The Hub, whose
// pills DO submit, filters those out instead — see AssistantHub.
const composer = useTemplateRef<{ setText: (v: string) => void }>("composer");
function useSample(s: string): void {
  composer.value?.setText(s);
}

// The empty state wears the assistant's own colour + icon rather than the
// generic aurora mark, so the chat surface and its Hub tile read as the same
// thing. Same helpers the Hub grid uses.
const chatTint = computed<string>(() => assistantTint(assistantSlug.value));
const chatIcon = computed(() =>
  selectedAssistant.value
    ? resolveAssistantIcon(iconOf(selectedAssistant.value))
    : null,
);

const placeholder = computed<string>(() => {
  const name = selectedAssistant.value?.name;
  return name ? `Ask ${name}…` : "Ask a question…";
});

function nextId(): string {
  return `t${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function resetTurns() {
  turns.splice(0, turns.length);
  conversationId.value = null;
}

const { confirm } = useConfirm();

async function onSubmit(message: string) {
  if (busy.value) return;
  // Sending a message means "show me the answer": follow it from the start.
  followBottom.value = true;

  // Let a just-attached file land first, or the answer is written without
  // it (see inflightUploads). A failed upload still lets the message go —
  // its own status line says what went wrong.
  while (inflightUploads.size) {
    uploadStatus.value = "Waiting for your attachment to finish uploading…";
    await Promise.allSettled([...inflightUploads]);
  }

  // AI001 §7 — warn (never block) when the draft looks like it carries a
  // special-category identifier. A hard block on a false positive just
  // sends the same text to a consumer chatbot instead, which is strictly
  // worse; a conscious "yes, send it" is the control we actually want.
  const check = await checkPrompt(message, selectedUser.value);
  if (check.flagged) {
    const proceed = await confirm({
      title: "Personfølsomme oplysninger?",
      message: `${check.message} Vil du sende beskeden alligevel?`,
      confirmLabel: "Send alligevel",
      cancelLabel: "Rediger besked",
      danger: true,
    });
    if (!proceed) {
      composer.value?.setText(message);
      return;
    }
  }

  turns.push({ id: nextId(), role: "user", content: message });
  turns.push({
    id: nextId(),
    role: "assistant",
    content: "",
    streaming: true,
  });

  // CRITICAL: read the reactive-proxy reference back out of `turns` instead of
  // holding the raw object we just pushed. Mutating via the raw target bypasses
  // the proxy's set trap, so Vue never re-renders the bubble.
  const assistant = turns[turns.length - 1];

  busy.value = true;
  abort.value = new AbortController();
  try {
    const started = await sendChat({
      message,
      devUser: selectedUser.value,
      assistantSlug: assistantSlug.value || undefined,
      conversationId: conversationId.value ?? undefined,
      // Carry the live KB-picker selection so a KB chosen before the first
      // message still scopes that first answer. Only when the assistant
      // actually reads KBs — otherwise leave it unset (backend no-op).
      kbIds: kbPickerEnabled.value ? conversationKbIds.value : undefined,
      signal: abort.value.signal,
    });
    if (started.conversationId !== null) {
      conversationId.value = started.conversationId;
    }
    for await (const evt of started.events) {
      if (evt.type === "retrieval") {
        // Tool-loop turns emit one retrieval frame PER tool call with
        // globally-unique hit.n — merge instead of replace, or every
        // frame after the first loses its citation chips and the
        // sources pane under-reports (2026-07-29 fix).
        const prev = assistant.retrieval;
        if (prev && prev.hits.length) {
          const seen = new Set(prev.hits.map((h) => h.n));
          assistant.retrieval = {
            ...evt,
            hits: [...prev.hits, ...evt.hits.filter((h) => !seen.has(h.n))],
          };
        } else {
          assistant.retrieval = evt;
        }
      } else if (evt.type === "delta") {
        assistant.content += evt.content;
      } else if (evt.type === "tool_call") {
        // ADR 0017 Phase 3 — record the call so MessageBubble can
        // render a "ran SQL / searched" card. We append eagerly and
        // back-fill the result when the matching tool_result lands.
        if (!assistant.tools) assistant.tools = [];
        assistant.tools.push({
          id: evt.id,
          name: evt.name,
          arguments: evt.arguments,
        });
      } else if (evt.type === "tool_result") {
        const rt = (assistant.tools ?? []).find((t) => t.id === evt.id);
        if (rt) rt.result = evt;
      } else if (evt.type === "workflow") {
        // Per-assistant workflow router matched one or more workflow
        // slugs for this turn; render a badge on the assistant
        // bubble so the operator can see which framework the LLM
        // is following.
        assistant.matchedWorkflows = evt.matched;
      } else if (evt.type === "error") {
        assistant.error = evt.message;
      } else if (evt.type === "persisted") {
        // Task #294 — the backend persisted the assistant message
        // and is handing us back its DB row id so the feedback
        // buttons can address it without a full conversation
        // rehydrate. The `done` frame arrives right after this.
        const mid = (evt as { message_id?: unknown }).message_id;
        if (typeof mid === "number") assistant.messageId = mid;
      } else if (evt.type === "done") {
        break;
      }
    }
  } catch (err) {
    assistant.error = humaniseChatError(err);
    // 428: the onboarding video is due (re-issued, or the profile was stale).
    // Re-reading the profile brings the video up.
    if (err instanceof ChatError && err.status === 428) void refreshMyProfile();
    // Remember the user's last question so the "Try again" button in
    // the error bubble can resend without retyping. Capturing this on
    // the assistant turn keeps it tied to the bubble lifecycle.
    assistant.retryMessage = message;
  } finally {
    assistant.streaming = false;
    busy.value = false;
    abort.value = null;
    // A turn can create files — create_document writes into the bid
    // project's folders — so the file panels are stale the moment it
    // ends. Bumping only after an upload (as this did) meant a generated
    // kravmatrix never appeared in the tree, and the project download,
    // which only shows once something has been generated, stayed hidden.
    // In the `finally` so a turn that errors after building a document
    // still surfaces it.
    uploadsRefreshKey.value += 1;
    convListRefresh.value += 1;
    // The spend meter only moves when THIS user sends a turn, so this is the
    // one moment it can be stale. Cheaper and more accurate than shortening
    // the poll interval, which would spend requests on the 99% of time
    // nothing changed. Optional-chained: the meter renders nothing when
    // budgeting is off, so the ref may be null.
    budgetMeter.value?.refresh();
  }
}

/** Map low-level error shapes to user-facing copy. Raw `err.message`
 * surfaces things like "TypeError: Failed to fetch" or "AbortError"
 * which are useless to a human. */
function humaniseChatError(err: unknown): string {
  if (err instanceof ChatError) {
    // Append the correlation id so the user has something concrete to
    // quote. Without it "chat broke" is unsearchable and an operator has
    // to guess from timestamps (ADR 0041).
    return err.requestId
      ? `${err.message} (reference: ${err.requestId})`
      : err.message;
  }
  if (err instanceof DOMException && err.name === "AbortError") {
    return "Stopped before the answer was finished.";
  }
  const msg = err instanceof Error ? err.message : String(err);
  if (/failed to fetch|networkerror|network request failed/i.test(msg)) {
    return "Couldn't reach the AI backend. Check your network and try again.";
  }
  if (/timeout/i.test(msg)) {
    return "The AI took too long to respond. Try again or simplify the question.";
  }
  return msg || "Something went wrong on our side. Try again in a moment.";
}

/** Resend the last failed message. Called by MessageBubble's
 * try-again affordance when a turn ends in an error. */
function retryTurn(message: string) {
  if (busy.value) return;
  onSubmit(message);
}

function stop() {
  abort.value?.abort();
}

async function selectConversation(id: number) {
  if (busy.value) return;
  chatNavOpen.value = false;
  followBottom.value = true;
  const loaded = await getConversation(id, selectedUser.value);
  if (!loaded) return;
  resetTurns();
  conversationId.value = id;
  assistantSlug.value = loaded.conversation.assistant_slug;
  conversationKbIds.value = loaded.conversation.kb_ids ?? [];
  // Task #294 — feedback map keyed by message_id. The backend's
  // get_conversation response includes the caller's existing votes
  // on every message in this conversation (`feedback: { "<msg_id>":
  // { kind, comment, updated_at } }`); rehydrate so reopening a past
  // conversation shows the existing 👍/👎 + saved note.
  const feedbackByMsgId =
    (
      loaded as {
        feedback?: Record<
          string,
          {
            kind: "up" | "down";
            comment: string | null;
            updated_at?: string;
          }
        >;
      }
    ).feedback ?? {};
  for (const m of loaded.messages) {
    if (m.role === "user") {
      turns.push({ id: `m${m.id}`, role: "user", content: m.content });
    } else if (m.role === "assistant") {
      const existingFb = feedbackByMsgId[String(m.id)];
      turns.push({
        id: `m${m.id}`,
        role: "assistant",
        content: m.content,
        retrieval: (m.retrieval as RetrievalEvent | null) ?? undefined,
        tools: m.tools ?? undefined,
        messageId: m.id,
        feedback: existingFb
          ? {
              kind: existingFb.kind,
              comment: existingFb.comment,
              updated_at: existingFb.updated_at,
            }
          : undefined,
      });
    }
  }
}

function onFeedbackChanged(payload: {
  turnId: string;
  feedback: {
    kind: "up" | "down";
    comment: string | null;
    updated_at?: string;
  } | null;
}) {
  const t = turns.find((x) => x.id === payload.turnId);
  if (!t) return;
  t.feedback = payload.feedback ?? undefined;
}

function startNew() {
  if (busy.value) return;
  chatNavOpen.value = false;
  resetTurns();
  conversationKbIds.value = [];
}

/** Switching assistant from the chat header starts a FRESH conversation for
 *  that assistant — the same thing clicking its tile on the hub does.
 *
 *  It used to be a plain `v-model` on `assistantSlug`, which changed who was
 *  answering but left the previous conversation on screen. That reads as the
 *  new assistant having said all of it, and the next question continued an
 *  unrelated thread. The hub path already resets for exactly this reason
 *  ("clicking a tile means start something new here"); the dropdown was the
 *  one way into an assistant that skipped it.
 */
function onPickAssistant(slug: string): void {
  if (!slug || slug === assistantSlug.value) return;
  // AssistantPicker emits once on mount to default-select the first
  // assistant when none is chosen. That is not someone switching — adopt it
  // without resetting anything or rewriting the URL.
  if (!assistantSlug.value) {
    assistantSlug.value = slug;
    return;
  }
  // Mid-stream navigation is refused everywhere else (selectConversation,
  // startNew); stay consistent rather than abandoning a half-written answer.
  if (busy.value) return;
  startNew();
  assistantSlug.value = slug;
  // replaceState, not `location.hash = …`: assigning the hash fires
  // hashchange → applyRoute → a second resetTurns, and the sidebar sets the
  // assistant without touching the hash at all, so the hash can already read
  // #/chat/<other-slug> while a different assistant is active. Writing the
  // URL directly keeps it truthful without depending on an event that may
  // not fire.
  history.replaceState(null, "", `#/chat/${slug}`);
  route.value = parseHash();
}

// Follow the answer while it streams.
//
// This used to set `scroller.scrollTop` — but `#app` has `min-height:
// 100vh`, so the layout grows with the conversation and it is the PAGE that
// scrolls, not the transcript. The transcript never overflowed, the
// assignment did nothing, and a streaming answer ran off the bottom of the
// screen in every browser (reported from Safari, 2026-10-22). Scroll
// whichever element actually scrolls.
//
// And only while the reader is at the bottom: someone who scrolled up to
// re-read part of the answer must not be yanked back down by every token.
// `followBottom` changes only on a scroll the reader makes (or ours), never
// because content grew, so a large chunk arriving does not switch it off.
const FOLLOW_SLACK_PX = 120;
const followBottom = ref(true);
function scrollingEl(): HTMLElement {
  const s = scroller.value;
  if (s && s.scrollHeight > s.clientHeight + 1) return s;
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}
function onAnyScroll() {
  const el = scrollingEl();
  followBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK_PX;
}
onMounted(() => window.addEventListener("scroll", onAnyScroll, { passive: true }));
onUnmounted(() => window.removeEventListener("scroll", onAnyScroll));
// The transcript is rendered conditionally (hub, admin and chat share this
// shell), so attach to it whenever it appears rather than once at mount.
watch(scroller, (el, prev) => {
  prev?.removeEventListener("scroll", onAnyScroll);
  el?.addEventListener("scroll", onAnyScroll, { passive: true });
});
watch(
  () => turns.map((t) => t.content).join("|"),
  () => {
    if (!followBottom.value) return;
    nextTick(() => {
      const el = scrollingEl();
      el.scrollTop = el.scrollHeight;
    });
  },
);
</script>

<template>
  <div class="app-shell">
    <AuroraBackground />
    <ToastShelf />
    <ConfirmDialog />

    <PolicyGate
      v-if="needsPolicyAck && myProfile?.ai_policy"
      :version="myProfile.ai_policy.version"
      :url="myProfile.ai_policy.url"
      @acknowledged="onPolicyAcknowledged"
    />

    <OnboardingGate
      v-if="pendingVideo"
      :key="`${pendingVideo.id}@${pendingVideo.version}`"
      :video="pendingVideo"
      :dev-user="selectedUser || undefined"
      @completed="refreshMyProfile"
      @leave="leaveOnboarding"
    />

    <!-- ADR 0038 — SME "Adjust assistant" modal -->
    <div
      v-if="refineOpen && assistantSlug"
      class="refine-modal"
      @click.self="refineOpen = false"
    >
      <div class="refine-modal__card" role="dialog" aria-modal="true">
        <header class="refine-modal__head">
          <h3>Adjust {{ selectedAssistant?.name || assistantSlug }}</h3>
          <button
            class="refine-modal__close"
            aria-label="Close"
            @click="refineOpen = false"
          >
            ×
          </button>
        </header>
        <RefineAssistant
          :key="assistantSlug + selectedUser"
          :slug="assistantSlug"
          :assistant-name="selectedAssistant?.name"
          :dev-user="selectedUser"
        />
      </div>
    </div>
    <BrandHeader>
      <template #actions>
        <!-- Dev-user picker lives in the header in local-dev mode so
             the operator can see + switch identity from every view
             (Hub / landing / chat / admin), not just the chat
             surface. Production builds (USE_REAL_AUTH=true) hide
             this entirely; the MSAL login button is the equivalent. -->
        <UserPicker
          v-if="devPickerVisible"
          :users="DEV_USERS"
          :selected="selectedUser"
          @select="selectedUser = $event"
        />
        <!-- Spend meter (ADR 0049). Renders nothing until the user is
             near or past their limit, so the nav stays clean for the
             large majority who never approach it. -->
        <BudgetMeter ref="budgetMeter" :dev-user="selectedUser" />
        <!-- ADR 0038 — SME co-develop entry, right where they saw the
             answer they want changed. Only rendered when the backend
             confirmed the caller may refine this assistant. -->
        <button
          v-if="route.name === 'chat' && canRefine"
          class="admin-link"
          @click="refineOpen = true"
        >
          Adjust assistant
        </button>
        <button
          v-if="route.name !== 'hub' && route.name !== 'admin'"
          class="admin-link"
          @click="gotoHub"
        >
          Hub
        </button>
        <!-- Knowledge Bases — a real destination (per-user KB manager,
             ADR 0019), so it lives in the nav rather than as a tile in the
             assistant grid. -->
        <button v-if="route.name !== 'kbs'" class="admin-link" @click="gotoKbs">
          Knowledge Bases
        </button>
        <!-- Contracts dashboard link — visible only to admins +
             `group:contracts-confidential` members (mirrors the
             /api/contracts/dashboard backend gate). Server-side is
             still the authoritative gate; this just hides the
             affordance for users who'd get a 403 anyway. -->
        <button
          v-if="canSeeContracts && route.name !== 'contracts'"
          class="admin-link"
          @click="gotoContracts"
        >
          Contracts
        </button>
        <!-- Admin is reachable from Hub + chat + landing so operators
             don't need to type the #/admin hash to find it. The
             backend gates every admin endpoint with the admin group
             check, so a non-admin clicking this lands on a 403 (toast).
             Frontend gate is "hide when we KNOW the user is not admin"
             (i.e. profile loaded and is_admin === false); when the
             profile hasn't arrived yet, or the backend image predates
             /api/profile.is_admin, we keep showing the button — the
             server is still the authoritative gate. -->
        <button
          v-if="mySignOffCount > 0 && route.name !== 'signoffs'"
          class="admin-link"
          @click="gotoSignOffs"
        >
          My sign-offs
          <span class="nav-count">{{ mySignOffCount }}</span>
        </button>
        <button
          v-if="
            (myProfile?.is_admin !== false || canOpenAdmin(myProfile)) &&
            route.name !== 'admin'
          "
          class="admin-link"
          @click="gotoAdmin"
        >
          Admin
        </button>
      </template>
    </BrandHeader>

    <!-- Profile is already loaded here for the nav gates; pass the two bits
         the Hub needs (greeting name + default ask-bar target) rather than
         making it fetch /api/profile a second time. -->
    <AssistantHub
      v-if="route.name === 'hub'"
      :dev-user="selectedUser"
      :display-name="myProfile?.display_name"
      :preferred-slug="myProfile?.preference?.default_assistant_slug"
    />

    <AssistantLanding
      v-else-if="route.name === 'landing'"
      :slug="route.slug"
      :dev-user="selectedUser"
    />

    <AdminPage
      v-else-if="route.name === 'admin'"
      :dev-user="selectedUser"
      :read-only="isReadOnlyProfile(myProfile)"
      @exit="exitAdmin"
    />

    <MySignOffs
      v-else-if="route.name === 'signoffs'"
      :dev-user="selectedUser"
      @exit="gotoHub"
      @changed="(n: number) => (mySignOffCount = n)"
    />

    <ProfileBuilder
      v-else-if="route.name === 'booking'"
      :dev-user="selectedUser"
      @exit="gotoHub"
    />

    <ContractsDashboard
      v-else-if="route.name === 'contracts'"
      :dev-user="selectedUser"
      @exit="gotoHub"
    />

    <KbManager
      v-else-if="route.name === 'kbs'"
      :dev-user="selectedUser"
      :my-oid="myProfile?.oid ?? ''"
      :sharing-enabled="myProfile?.features?.kb_sharing ?? false"
      @exit="gotoHub"
    />

    <ServiceCatalogPage
      v-else-if="route.name === 'servicecatalog'"
      :dev-user="selectedUser"
      @exit="gotoHub"
    />

    <BidWorkspacePage
      v-else-if="route.name === 'bid'"
      :dev-user="selectedUser"
      :bid-uid="route.slug || null"
      @exit="gotoHub"
    />

    <DesignPreview v-else-if="route.name === 'preview'" />

    <div
      v-else
      class="app-body"
      :class="{
        'app-body--with-pane': sourcePane.state.pinned,
        'app-body--nav-open': chatNavOpen,
      }"
    >
      <!-- Mobile: tapping the scrim closes the conversation drawer. -->
      <div class="chat-nav-scrim" @click="chatNavOpen = false"></div>
      <ConversationList
        :dev-user="selectedUser"
        :active-id="conversationId"
        :refresh-key="convListRefresh"
        :current-assistant-slug="assistantSlug"
        :assistants="assistants"
        @select="selectConversation"
        @new="startNew"
      />
      <main class="app-main">
        <div class="app-main__controls">
          <!-- Mobile-only: opens the conversation list as a drawer. The
               list is an inline sidebar on desktop, so this is hidden there. -->
          <button
            type="button"
            class="chat-nav-toggle admin-link"
            aria-label="Show recent chats"
            @click="chatNavOpen = true"
          >
            ☰ Chats
          </button>
          <!-- UserPicker moved to the BrandHeader so it's visible
               from every route; this row keeps the assistant
               selector for the chat surface only. -->
          <AssistantPicker
            :model-value="assistantSlug"
            :dev-user="selectedUser"
            @update:model-value="onPickAssistant"
          />
        </div>

        <section ref="scroller" class="transcript glass" aria-live="polite">
          <!-- Empty state: say which assistant you're in, then give the
               operator's own questions as the thing to click. The old copy
               ("Answers are grounded in the content this assistant can
               access, with citations…") said in two lines what the
               citations themselves demonstrate on the first answer, and it
               left the only actionable element far below the fold of the
               eye's resting point. -->
          <div v-if="turns.length === 0" class="transcript__empty">
            <span
              class="transcript__empty-mark"
              aria-hidden="true"
              :style="{ '--tint': chatTint }"
            >
              <component
                :is="chatIcon"
                v-if="chatIcon"
                :size="26"
                :stroke-width="1.8"
              />
            </span>
            <h2 class="transcript__empty-title display">
              {{ selectedAssistant?.name ?? "Ask anything" }}
            </h2>
            <div v-if="samples.length" class="transcript__empty-samples">
              <button
                v-for="s in samples"
                :key="s"
                type="button"
                class="transcript__empty-sample"
                :style="{ '--tint': chatTint }"
                @click="useSample(s)"
              >
                {{ s }}
              </button>
            </div>
          </div>
          <MessageBubble
            v-for="t in turns"
            :key="t.id"
            :turn="t"
            :conversation-id="conversationId ?? undefined"
            :dev-user="selectedUser"
            @retry="retryTurn"
            @feedback-changed="onFeedbackChanged"
          />
        </section>

        <BidProjectPanel
          v-if="assistantSlug === 'bid-writing'"
          :conversation-id="conversationId"
          :dev-user="selectedUser"
          :refresh-key="uploadsRefreshKey"
        />
        <UploadedDocsPanel
          v-if="uploadsEnabled && assistantSlug !== 'bid-writing'"
          :conversation-id="conversationId"
          :dev-user="selectedUser"
          :refresh-key="uploadsRefreshKey"
        />
        <KbPicker
          v-if="kbPickerEnabled"
          v-model="conversationKbIds"
          :conversation-id="conversationId"
          :dev-user="selectedUser"
          @error="onKbPickerError"
        />
        <!-- Upload status sits ABOVE the composer, next to the file chips it
             describes. Below it, it was the last element on the page and the
             easiest thing on screen to miss — on a laptop it could be under
             the fold entirely, so "Uploading…" went unnoticed and the upload
             looked like nothing had happened. aria-live announces it without
             moving focus. -->
        <div
          v-if="uploadStatus"
          class="upload-toast"
          role="status"
          aria-live="polite"
        >
          {{ uploadStatus }}
        </div>
        <ChatInput
          ref="composer"
          :disabled="busy"
          :placeholder="placeholder"
          :uploads-enabled="uploadsEnabled"
          :folder-uploads-enabled="folderUploadsEnabled"
          @submit="onSubmit"
          @upload="onUpload"
        />
        <button v-if="busy" class="stop" @click="stop">Stop streaming</button>
      </main>
      <SourcePreviewPane
        v-if="sourcePane.state.pinned"
        class="app-source-pane"
        :dev-user="selectedUser"
        :assistant-slug="assistantSlug"
      />
    </div>
  </div>
</template>

<style scoped>
.app-body {
  display: flex;
  gap: var(--space-4);
  align-items: stretch;
  min-height: 0; /* so the transcript flex child can scroll */
}
.app-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  min-height: 0; /* let `.transcript: flex: 1` grow + clip */
}
/* Phase D source-preview pane — fixed-width right rail when pinned.
   The chat column above shrinks naturally (`flex: 1` + `min-width: 0`)
   so the pane never overlays content; it splits horizontally. On
   narrow viewports we drop to ~ 320px to keep the chat usable; on
   wider screens up to 460px reads comfortably. */
.app-source-pane {
  flex: 0 0 clamp(20rem, 32vw, 30rem);
  min-height: 0;
  overflow: hidden;
  border-radius: var(--radius-md);
  background: var(--color-surface);

  /* Pinned to the viewport, not to the top of the transcript.
     Answers run long, and the citation that opened the pane is usually
     far down one. With the pane parked at the top of a tall flex row the
     reader had to scroll up to see the source — losing their place in the
     answer — and then scroll back. Verification is a comparison task, so
     the claim and its evidence have to be on screen together.

     `align-self: flex-start` is load-bearing: the row's default
     `align-items: stretch` would size the pane to the full row height,
     leaving `sticky` nothing to travel within. */
  align-self: flex-start;
  position: sticky;
  top: var(--space-4);
  max-height: calc(100dvh - var(--space-4) * 2);
}
/* The Chats list stays in view while a long conversation scrolls the page
   (reported 2026-10-22: it scrolled away with the chat). Same mechanism as
   the source pane above — `align-self: flex-start` gives `sticky` room to
   travel; the row's default stretch would not. Desktop only: below 720px
   the list is the off-canvas drawer and is positioned `fixed` instead. */
@media (min-width: 721px) {
  .app-body :deep(.convos) {
    align-self: flex-start;
    position: sticky;
    top: var(--space-4);
    max-height: calc(100dvh - var(--space-4) * 2);
  }
  .app-body :deep(.convos__groups) {
    /* Fill the sticky panel instead of a fixed 70vh, minus the header. */
    max-height: calc(100dvh - var(--space-4) * 2 - 5rem);
  }
}
.app-main__controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
}
.transcript {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  /* Fill the available vertical space — the shell's grid row gives us the
     height budget. `min-height: 0` lets the flex child actually shrink so
     `overflow-y: auto` engages. */
  flex: 1;
  min-height: 20rem;
  overflow-y: auto;
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  align-items: stretch;
}
/* Cap inner content to a comfortable reading column. The
   transcript box still fills the column it sits in (so the
   citation pane doesn't slide off-screen), but the bubble
   stream + sample chips center within a reading-width band.
   When the citation pane IS pinned (.app-body--with-pane on
   the parent), we keep it tight at 74ch so the layout doesn't
   feel crowded. When the pane is closed, give the content
   more horizontal room — 96ch handles a wide table without
   wrapping a column out of view. */
.transcript > * {
  width: 100%;
  max-width: 96ch;
  margin-left: auto;
  margin-right: auto;
}
.app-body--with-pane .transcript > * {
  max-width: 74ch;
}
.transcript__empty {
  margin: auto;
  max-width: 42rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  text-align: center;
  padding: var(--space-6) var(--space-4);
}
.transcript__empty-mark {
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  border-radius: 30%;
  background: color-mix(in srgb, var(--tint) 13%, transparent);
  color: var(--tint);
}
.transcript__empty-title {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 500;
  color: var(--color-ink);
}
/* The operator's own questions, one click from being asked. This is the
   whole point of the empty state — a blank transcript with nothing to press
   is the least useful screen in the app. */
.transcript__empty-samples {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.45rem;
  margin-top: var(--space-2);
}
.transcript__empty-sample {
  padding: 0.45rem 0.85rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--glass-border);
  background: color-mix(in srgb, var(--color-surface) 55%, transparent);
  color: var(--color-muted);
  font-size: 0.86rem;
  line-height: 1.35;
  cursor: pointer;
  transition:
    color var(--dur-1) var(--ease-out),
    border-color var(--dur-1) var(--ease-out),
    background var(--dur-1) var(--ease-out);
}
.transcript__empty-sample:hover {
  color: var(--color-ink);
  background: var(--color-surface);
  border-color: color-mix(in srgb, var(--tint) 35%, transparent);
}
.upload-toast {
  /* Sits directly above the composer, so the margin goes below it. */
  margin: 0.25rem 0 0.4rem;
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-muted);
}
.stop {
  align-self: flex-start;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  color: var(--color-error);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  background: transparent;
}
.stop:hover {
  background: rgb(183 28 28 / 0.08);
}
/* The conversation list is an inline sidebar on desktop; the drawer
   scaffolding (toggle button + scrim) only exists on mobile. */
.chat-nav-toggle {
  display: none;
}
.chat-nav-scrim {
  display: none;
}

@media (max-width: 720px) {
  .app-body {
    flex-direction: column;
  }
  /* Stacked below the transcript the pane is off-screen exactly when it is
     wanted. Too narrow to sit beside the answer, so it becomes a bottom
     sheet over it — dismissed with the pane's own × or Esc. */
  .app-source-pane {
    position: fixed;
    inset: auto 0 0 0;
    z-index: 190;
    flex: none;
    align-self: auto;
    max-height: 70dvh;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    box-shadow: var(--shadow-lg);
  }
  /* Show the "☰ Chats" trigger in the controls row. */
  .chat-nav-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  /* The conversation list slides in from the left as an off-canvas drawer
     instead of stacking above the chat and burying the composer. */
  .app-body :deep(.convos) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 200;
    width: min(20rem, 86vw);
    max-height: 100dvh;
    padding: var(--space-4);
    background: var(--color-surface);
    box-shadow: var(--shadow-lg);
    border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
    transform: translateX(-102%);
    transition: transform var(--dur-2) var(--ease-out);
    overflow-y: auto;
  }
  .app-body--nav-open :deep(.convos) {
    transform: translateX(0);
  }
  /* Let the list fill the drawer height rather than its desktop 70vh cap. */
  .app-body :deep(.convos__groups) {
    max-height: none;
  }
  /* Dim + click-to-close backdrop behind the open drawer. */
  .app-body--nav-open .chat-nav-scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 199;
    background: rgb(0 0 0 / 0.42);
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
  }
}
.nav-count {
  margin-left: 0.3rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  background: color-mix(in srgb, var(--color-warn) 20%, transparent);
  color: var(--color-warn-ink);
}
.admin-link {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 0.35rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--color-ink);
  cursor: pointer;
  transition:
    background var(--dur-1) var(--ease-out),
    box-shadow var(--dur-1) var(--ease-out);
}
.admin-link:hover {
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

/* ADR 0038 — SME refine modal */
.refine-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 25, 35, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 6vh 1rem 1rem;
  z-index: 60;
}
.refine-modal__card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  width: min(720px, 100%);
  max-height: 86vh;
  overflow: auto;
  padding: 1rem 1.25rem 1.25rem;
}
.refine-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.refine-modal__head h3 {
  margin: 0;
}
.refine-modal__close {
  border: none;
  background: none;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: var(--color-muted);
}
</style>
